import { Divider } from "components/design/Divider";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { Filter } from "components/DesignSystem/Filter/Filter";
import { DateFilter } from "components/Filters/DateFilter";
import { PaperWithPencil } from "components/icons/PaperWithPencil";
import {
  CLICKED_FILTER_ON_FILINGS,
  CLICKED_PENDING_ON_ME_ON_FILINGS,
} from "constants/analyticsEvents";
import { FILTER_FILING_PENDING_ON_ME } from "constants/task";
import { useAnalytics } from "hooks/useAnalytics";
import { formatDate } from "utils/formatDate";
import { TaskListFilterType } from "./TaskList";
import { BaseTaskTemplateFilter } from "./BaseTaskTemplateFilter";

const DateFilterChip = ({
  startDate,
  endDate,
  updateFilter,
}: {
  startDate: string;
  endDate: string;
  updateFilter: TaskListFilterType["updateFilter"];
}) => {
  const handleDateClose = () => {
    updateFilter("START_DATE", "");
    updateFilter("END_DATE", "");
    updateFilter("SELECT_PERIOD", "");
  };

  if (startDate && endDate) {
    return (
      <Chip onRemove={handleDateClose} isActive filterType="DATE">
        From: {formatDate(startDate)} To: {formatDate(endDate)}
      </Chip>
    );
  }

  if (startDate) {
    return (
      <Chip
        onRemove={() => {
          updateFilter("START_DATE", "");
        }}
        isActive
        filterType="DATE"
      >
        From: {formatDate(startDate)}
      </Chip>
    );
  }
  if (endDate) {
    return (
      <Chip
        onRemove={() => {
          updateFilter("END_DATE", "");
        }}
        isActive
        filterType="DATE"
      >
        To: {formatDate(endDate)}
      </Chip>
    );
  }
};

export const TaskListFilter = ({
  values,
  updateFilter,
}: TaskListFilterType) => {
  const { trackEvent } = useAnalytics();
  const isFilingSelected = values.FILINGS.length > 0;
  const isDateSelected = Boolean(values.START_DATE || values.END_DATE);
  const isAnyRemoveableFilterSelected = isFilingSelected || isDateSelected;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      trackEvent(CLICKED_FILTER_ON_FILINGS);
    }
  };

  const handlePendingOnMeClick = () => {
    trackEvent(CLICKED_PENDING_ON_ME_ON_FILINGS);
    updateFilter(FILTER_FILING_PENDING_ON_ME, true);
  };

  return (
    <Filter.Root
      defaultValue="FILINGS"
      title=""
      hideDivider
      withWrapper={false}
      onOpenChange={handleOpenChange}
      capsule={
        <>
          <Chip
            onRemove={() => updateFilter(FILTER_FILING_PENDING_ON_ME, false)}
            isActive={Boolean(values.PENDING_ON_ME)}
            filterType={FILTER_FILING_PENDING_ON_ME}
            onClick={handlePendingOnMeClick}
            isFixedFilter
            preventOpenOnClick
          >
            <span className="t-flex t-gap-1 t-items-center">
              <PaperWithPencil />
              Pending on me
            </span>
          </Chip>
          {isAnyRemoveableFilterSelected && <Divider orientation="vertical" />}
          {values.FILINGS.length > 0 && (
            <Chip
              onRemove={() => updateFilter("FILINGS", [])}
              isActive
              filterType="FILINGS"
            >
              Filings ({values.FILINGS.length})
            </Chip>
          )}
          <DateFilterChip
            startDate={values.START_DATE}
            endDate={values.END_DATE}
            updateFilter={updateFilter}
          />
        </>
      }
    >
      <Filter.Portal>
        <Filter.List>
          <Filter.ListItem value="FILINGS">Filings</Filter.ListItem>
          <Filter.ListItem value="DATE">Deadline</Filter.ListItem>
        </Filter.List>
        <Filter.Body value="FILINGS" block>
          <BaseTaskTemplateFilter
            selectedTemplateFilter={values.FILINGS.join(",")}
            onUpdate={(values) => updateFilter("FILINGS", values)}
          />
        </Filter.Body>
        <Filter.Body value="DATE" block>
          <DateFilter
            values={values}
            updateFilter={updateFilter}
            maxEndDate={null}
          />
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};
